/**
 * Minimal hash router for the menu screens.
 *   #/                → main menu
 *   #/singleplayer    → singleplayer setup
 *   #/multiplayer     → lobby browser
 *   #/lobby/<CODE>    → lobby (joins on load)
 *   #/replays, #/stats, #/help, #/editor
 *   #/ladder (Ranking), #/daily (daily challenge)     wave 4 (P15)
 *   #/campaign (the walkthrough, "Campaign")          wave 4 stage J
 *   #/privacy (analytics privacy page)                wave 4 stage K
 * The in-game screen is not URL-addressable (a refresh returns to the menu).
 * `?lobby=CODE` invite links are converted into the lobby route on startup.
 */
import { signal } from "@preact/signals";

export type Route =
  | { name: "menu" }
  | { name: "singleplayer" }
  | { name: "multiplayer" }
  | { name: "lobby"; lobbyID: string }
  | { name: "game" }
  | { name: "replays" }
  | { name: "stats" }
  | { name: "help" }
  | { name: "editor" }
  | { name: "ladder" }
  | { name: "daily" }
  | { name: "campaign" }
  // Wave 4 stage K (analytics): the privacy page.
  | { name: "privacy" }
  // Wave 4 stage L (store): the shop.
  | { name: "store" }
  // Wave 4 stage L (accounts): the account screen.
  | { name: "account" }
  // Wave 4 stage L (cosmetics): the collection screen and the custom-creation editors.
  | { name: "cosmetics" }
  // Wave 4 stage L (community): friends / parties / maps / replays, and the share links.
  | { name: "community" }
  // Wave 4 stage L (progression): levels, season pass, achievements, weekly challenge.
  | { name: "progress" }
  | { name: "party"; token: string }
  | { name: "friend"; token: string }
  | { name: "replay"; replayID: string }
  // Wave 4 stage L (admin): the admin panel.
  | { name: "admin" };

export type RouteName = Route["name"];

const LOBBY_CODE_RE = /^[A-Za-z0-9_-]{3,32}$/;

export function normalizeLobbyCode(code: string): string | null {
  const c = code.trim();
  return LOBBY_CODE_RE.test(c) ? c : null;
}

export function parseHash(hash: string): Route {
  const path = hash.replace(/^#\/?/, "").split("?")[0];
  const [head, arg] = path.split("/");
  switch (head) {
    case "singleplayer":
    case "multiplayer":
    case "replays":
    case "stats":
    case "help":
    case "editor":
    case "ladder":
    case "daily":
    case "campaign":
    case "privacy":
    case "store":
    case "account":
    case "admin":
    case "cosmetics":
    case "progress":
      return { name: head };
    // Wave 4 stage L (community).
    case "community":
      return { name: "community" };
    case "party":
    case "friend":
    case "replay": {
      const id = arg ? normalizeLobbyCode(decodeURIComponent(arg)) : null;
      if (!id) return { name: "community" };
      return head === "replay" ? { name: "replay", replayID: id } : { name: head, token: id };
    }
    case "lobby": {
      const code = arg ? normalizeLobbyCode(decodeURIComponent(arg)) : null;
      return code ? { name: "lobby", lobbyID: code } : { name: "multiplayer" };
    }
    default:
      return { name: "menu" };
  }
}

export function routeToHash(r: Route): string {
  switch (r.name) {
    case "menu":
    case "game":
      return "#/";
    case "lobby":
      return `#/lobby/${encodeURIComponent(r.lobbyID)}`;
    case "party":
    case "friend":
      return `#/${r.name}/${encodeURIComponent(r.token)}`;
    case "replay":
      return `#/replay/${encodeURIComponent(r.replayID)}`;
    default:
      return `#/${r.name}`;
  }
}

function initialRoute(): Route {
  if (typeof location === "undefined") return { name: "menu" };
  try {
    const params = new URLSearchParams(location.search);
    const invite = params.get("lobby");
    if (invite) {
      const code = normalizeLobbyCode(invite);
      // Strip the query so a refresh does not re-join forever.
      params.delete("lobby");
      const qs = params.toString();
      history.replaceState(null, "", location.pathname + (qs ? "?" + qs : "") + (code ? `#/lobby/${code}` : location.hash));
      if (code) return { name: "lobby", lobbyID: code };
    }
  } catch {
    /* ignore */
  }
  return parseHash(location.hash);
}

export const route = signal<Route>(initialRoute());

export function navigate(r: Route, opts: { replace?: boolean } = {}): void {
  route.value = r;
  if (typeof history === "undefined") return;
  const hash = routeToHash(r);
  if (location.hash === hash || (hash === "#/" && (location.hash === "" || location.hash === "#"))) return;
  try {
    if (opts.replace) history.replaceState(null, "", location.pathname + location.search + hash);
    else history.pushState(null, "", location.pathname + location.search + hash);
  } catch {
    /* ignore */
  }
}

/** Keep the route in sync with browser back / forward. Returns an unsubscribe. */
export function installRouterListeners(onBeforeChange?: (next: Route) => boolean): () => void {
  const handler = () => {
    const next = parseHash(location.hash);
    if (route.value.name === "game") {
      // Leaving a running game through the back button is handled by the game screen.
      if (onBeforeChange && !onBeforeChange(next)) {
        history.pushState(null, "", location.pathname + location.search + routeToHash(route.value));
        return;
      }
    }
    route.value = next;
  };
  window.addEventListener("popstate", handler);
  return () => window.removeEventListener("popstate", handler);
}

/** Shareable invite link for a lobby code. */
export function inviteLink(lobbyID: string): string {
  if (typeof location === "undefined") return `?lobby=${lobbyID}`;
  return `${location.origin}${location.pathname}?lobby=${encodeURIComponent(lobbyID)}`;
}
